import { defineStore } from 'pinia';
import { NOTIFICATION_UNKNOWN_ERROR, Store } from '@/setup/globals';
import i18n from '@/setup/i18n';
import { setInitialData } from '@/stores/plugins/initial-data';

export type StoreState = {
  notifications: Record<string, unknown>[];
  id: number;
};

const storeName = Store.Notification;

export default defineStore(storeName, {
  state: () => {
    const initialData: Partial<StoreState> = window.initialData?.[storeName] || {};
    const state: StoreState = {
      notifications: [],
      id: 1,
    };

    setInitialData(state, initialData);

    const updatedNotifications: Record<string, unknown>[] = [];
    let id: number = 1;

    state.notifications.forEach((notification) => {
      updatedNotifications.push({
        ...notification,
        id,
        expire: notification.expire !== false,
        delay: notification.delay || notification.message?.delay || 3,
      });

      id++;
    });

    state.notifications = updatedNotifications;
    state.id = id;

    return state;
  },
  getters: {
    getSelectorNotifications: (state) => state.notifications.filter(({ message }) => message?.source?.selector),
    getNonSelectorNotifications: (state) =>
      state.notifications.filter(({ message }) => !message.source || !message.source.selector),
    getGlobalNotifications: (state) => state.notifications.filter(({ message }) => !message.source),
    getNotifications: (state) => state.notifications,
    getFieldNotifications: (state) => state.notifications.filter(({ message }) => message?.source?.field),
  },
  actions: {
    /**
     * Adds a notification.
     */
    pushNotification(options: Record<string, unknown>) {
      if (!options) {
        return;
      }

      const notification = {
        ...options,
        id: this.id,
        expire: options.expire !== false,
        delay: options.delay || options.message?.delay || 3,
      };

      this.notifications.unshift(notification);

      this.id += 1;
    },

    /**
     * Removes a notification.
     */
    popNotification(id: number) {
      this.notifications = this.notifications.filter((notification) => notification.id !== id);
    },

    /**
     * Flushes field notifications.
     */
    flushFieldNotifications() {
      this.notifications = this.notifications.filter(
        (notification) => !notification.message.source || !notification.message.source.field,
      );
    },

    /**
     * Resets the notifications array in the current module state to an empty array.
     */
    resetNotifications() {
      this.notifications = [];
    },

    /**
     * Adds an "unknown error" to the notification stack.
     */
    showUnknownError() {
      const error = { ...NOTIFICATION_UNKNOWN_ERROR };

      error.message.message = i18n.global.t(error.message.translationKey);

      this.pushNotification(error);
    },

    /**
     * Shows the given notification and returns its instance.
     */
    showNotification(notification: Record<string, unknown>) {
      this.pushNotification(notification);

      return this.notifications.find((entry) => entry.id === this.id);
    },
  },
});
